"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, HardHat, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const specifications = [{
  title: "Accredited & compliant",
  desc: "NASC and CHAS certified site standards",
  icon: ShieldCheck
}, {
  title: "Operational experience",
  desc: "Guided by Pete Wood's 20+ years of frontline scaffolding leadership",
  icon: HardHat
}, {
  title: "Rapid turnaround",
  desc: "Clear itemised quotations, method statements, and site visits",
  icon: FileText
}];
export default function Quote() {
  const prefersReducedMotion = useReducedMotion();
  const animationProps = prefersReducedMotion ? {
    initial: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.45,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="request-a-quote" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden border-t border-border py-20 lg:py-28">
      {/* Structural background datum accents */}
      <div className="pointer-events-none absolute inset-0 opacity-15" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <motion.div {...animationProps} className="mx-auto max-w-3xl text-center">
          {/* Datum rule eyebrow */}
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3.5 py-1 text-xs font-semibold tracking-wide text-foreground uppercase">
            <span className="size-2 rounded-full bg-primary" />
            Main contractors &bull; Developers &bull; Commercial teams
          </div>

          <h2 className="font-default mt-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Ready to discuss your scaffolding specifications?
          </h2>

          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Speak directly with our estimating team for technical advice, site surveys, and structured commercial quotes across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.
          </p>

          {/* Action Trio */}
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto font-default font-semibold shadow-md transition-[color,background-color,border-color,box-shadow]">
              <Link to="/ContactRequestQuotePage">
                Request a Quote
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-default font-semibold transition-[color,background-color,border-color,box-shadow]">
              <a href="tel:01344929900" className="inline-flex items-center gap-2">
                <Phone className="size-4 text-primary" />
                Call Now: 01344 929900
              </a>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-default font-semibold transition-[color,background-color,border-color,box-shadow]">
              <Link to="/ContactRequestQuotePage">
                Discuss a Project
              </Link>
            </Button>
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            Direct operational contact &bull; Available Monday to Friday, 8am &ndash; 6pm
          </p>
        </motion.div>

        {/* Structural Specifications Strip */}
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {specifications.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={i} initial={prefersReducedMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: prefersReducedMotion ? 0 : i * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative rounded-lg border border-border bg-card/80 p-6 shadow-sm transition-[border-color,background-color]">
                <div className="flex items-start gap-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
                    <Icon className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-default text-base font-bold text-card-foreground">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-normal">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Accreditations Trust Bar */}
        <div className="mt-12 border-t border-border pt-8 text-center">
          <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
            Verified Operational Accreditations
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CheckCircle2 className="size-4 text-primary" />
              NASC Member
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CheckCircle2 className="size-4 text-primary" />
              CHAS Accredited Contractor
            </div>
          </div>
        </div>
      </div>
    </section>;
}